import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Chip,
  Card,
  CardContent,
  alpha
} from '@material-ui/core';
import {
  Refresh,
  Today,
  DateRange,
  CalendarToday,
  AttachMoney,
  CalendarTodayOutlined,
} from '@material-ui/icons';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import MaterialTable from 'material-table';

import { addDepense, updateDepense } from '../../redux/actions/depenseActions';
import FondService from '../../services/FondService';
import CustomizedDialogs from '../../components/dialog';
import DepenseForm from '../../components/DepenseForm';

const GestionDepenses = () => {
  const dispatch = useDispatch();
  const [depensesData, setDepensesData] = useState([]);
  const [statsPeriodes, setStatsPeriodes] = useState({
    aujourdhui: 0,
    hier: 0,
    mois_courant: 0,
    annee_courante: 0
  });
  const [intitules, setIntitules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedIntitule, setSelectedIntitule] = useState('');

  // Couleurs du thème
  const colors = {
    primary: '#226D68',
    secondary: '#f26522',
    success: '#2E7D32',
    warning: '#FF9800',
    error: '#D32F2F'
  };

  // Chargement des données
  const loadStatsPeriodes = async () => {
    try {
      setLoading(true);
      
      // Charger les statistiques pour chaque période
      const [aujourdhui, hier, mois, annee] = await Promise.all([
        FondService.depensesJournee(),
        FondService.depensesHier(),
        FondService.depensesMonth(),
        FondService.depensesYear()
      ]);

      setStatsPeriodes({
        aujourdhui: aujourdhui.data?.total || 0,
        hier: hier.data?.total || 0,
        mois_courant: mois.data?.total || 0,
        annee_courante: annee.data?.total || 0
      });

    } catch (error) {
      console.error('Erreur stats périodes:', error);
      toast.error('Erreur lors du chargement des statistiques');
    }
  };

  const loadDepenses = async (filters = {}) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.year) params.append('year', filters.year);
      if (filters.month) params.append('month', filters.month);
      if (filters.intitule) params.append('intitule', filters.intitule);
      
      const response = await FondService.getAllDepenses(params.toString());
      setDepensesData(response.data.data || response.data);
    } catch (error) {
      console.error('Erreur chargement dépenses:', error);
      toast.error('Erreur lors du chargement des dépenses');
    } finally {
      setLoading(false);
    }
  };

  const loadIntitules = async () => {
    try {
      const response = await FondService.getIntitules();
      setIntitules(response.data || []);
    } catch (error) {
      console.error('Erreur chargement intitulés:', error);
      // Si l'endpoint n'existe pas, on récupère les intitulés depuis les données
      const intitulesFromData = [...new Set(depensesData.map(item => item.intitule))].filter(Boolean);
      setIntitules(intitulesFromData);
    }
  };

  const refreshAllData = () => {
    loadStatsPeriodes();
    loadDepenses({ year: selectedYear });
    loadIntitules();
  };

  useEffect(() => {
    refreshAllData();
  }, []);

  // Gestion des filtres
  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    loadDepenses({ year });
  };

  const handleMonthChange = (event) => {
    const month = event.target.value;
    setSelectedMonth(month);
    loadDepenses({ 
      year: selectedYear, 
      month: month || undefined 
    });
  };

  const handleIntituleChange = (event) => {
    const intitule = event.target.value;
    setSelectedIntitule(intitule);
    loadDepenses({ 
      year: selectedYear, 
      month: selectedMonth || undefined,
      intitule: intitule || undefined
    });
  };

  const resetFilters = () => {
    setSelectedMonth('');
    setSelectedIntitule('');
    loadDepenses({ year: selectedYear });
  };

  // Composants réutilisables
  const StatCard = ({ title, value, subtitle, color, icon }) => (
    <Card sx={{ 
      textAlign: 'center', 
      p: 2,
      bgcolor: alpha(color, 0.1),
      border: `1px solid ${alpha(color, 0.2)}`,
      transition: 'all 0.2s ease',
      '&:hover': { transform: 'translateY(-2px)' }
    }}>
      <CardContent>
        <Box sx={{ color, mb: 1 }}>{icon}</Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color, mb: 1 }}>
          {typeof value === 'number' ? `${value.toLocaleString()} Fcfa` : value}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Configuration du tableau
  const columns = [
    { 
      title: "Intitulé", 
      field: "intitule",
      render: (row) => (
        <Box>
          <Typography fontWeight={600}>{row.intitule}</Typography>
          {row.commentaire && (
            <Typography variant="body2" color="text.secondary">
              {row.commentaire}
            </Typography>
          )}
        </Box>
      )
    },
    { 
      title: "Montant", 
      field: "montant",
      render: (row) => (
        <Typography fontWeight={700} color={colors.error}>
          {row.montant?.toLocaleString()} Fcfa
        </Typography>
      )
    },
    { 
      title: "Date", 
      field: "dateOper",
      render: (row) => (
        <Typography variant="body2">
          {new Date(row.dateOper).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Typography>
      )
    },
    { 
      title: "Utilisateur", 
      field: "speudo",
      render: (row) => (
        <Chip 
          label={row.speudo || 'Non spécifié'}
          size="small"
          variant="outlined"
          sx={{ borderColor: colors.primary, color: colors.primary }}
        />
      )
    }
  ];

  // Fonctions d'édition
  const handleRowAdd = (newData) => {
    return new Promise((resolve, reject) => {
      dispatch(addDepense(newData))
        .then(() => {
          refreshAllData();
          resolve();
        })
        .catch((error) => {
          toast.error('Erreur lors de l\'ajout');
          reject(error);
        });
    });
  };

  const handleRowUpdate = (newData, oldData) => {
    return new Promise((resolve, reject) => {
      dispatch(updateDepense(newData, oldData.idDepense))
        .then(() => {
          refreshAllData();
          resolve();
        })
        .catch((error) => {
          toast.error('Erreur lors de la modification');
          reject(error);
        });
    });
  };

  const handleRowDelete = (oldData) => {
    return new Promise((resolve, reject) => {
      // Implémentez la suppression selon votre configuration Redux
      // Pour l'instant, on rafraîchit simplement les données
      refreshAllData();
      resolve();
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* En-tête avec actions */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <CustomizedDialogs 
              title="Nouvelle dépense" 
              boutonTitle="Ajouter une dépense"
              icon="depense"
              refresh={refreshAllData}
            >
              <DepenseForm />
            </CustomizedDialogs>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Année</InputLabel>
                  <Select value={selectedYear} onChange={handleYearChange} label="Année">
                    {[2022, 2023, 2024, 2025].map(year => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mois</InputLabel>
                  <Select value={selectedMonth} onChange={handleMonthChange} label="Mois">
                    <MenuItem value="">Tous</MenuItem>
                    {Array.from({ length: 12 }, (_, i) => (
                      <MenuItem key={i + 1} value={i + 1}>
                        {new Date(2023, i).toLocaleString('fr-FR', { month: 'long' })}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Intitulé</InputLabel>
                  <Select value={selectedIntitule} onChange={handleIntituleChange} label="Intitulé">
                    <MenuItem value="">Tous</MenuItem>
                    {intitules.map((intitule, index) => (
                      <MenuItem key={index} value={intitule}>
                        {intitule}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={3}>
                <Button 
                  variant="outlined" 
                  onClick={resetFilters}
                  fullWidth
                  startIcon={<Refresh />}
                  size="small"
                  sx={{ borderColor: colors.primary, color: colors.primary }}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Statistiques par période */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress sx={{ color: colors.primary }} />
        </Box>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} md={3}>
            <StatCard
              title="Aujourd'hui"
              value={statsPeriodes.aujourdhui}
              subtitle={new Date().toLocaleDateString('fr-FR', { 
                day: '2-digit', 
                month: '2-digit' 
              })}
              color={colors.primary}
              icon={<Today />}
            />
          </Grid>
          
          <Grid item xs={6} md={3}>
            <StatCard
              title="Hier"
              value={statsPeriodes.hier}
              subtitle={new Date(Date.now() - 86400000).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit'
              })}
              color={colors.secondary}
              icon={<CalendarTodayOutlined />}
            />
          </Grid>
          
          <Grid item xs={6} md={3}>
            <StatCard
              title="Mois en cours"
              value={statsPeriodes.mois_courant}
              subtitle={new Date().toLocaleString('fr-FR', { month: 'long', year: 'numeric' })}
              color={colors.warning}
              icon={<DateRange />}
            />
          </Grid>
          
          <Grid item xs={6} md={3}>
            <StatCard
              title="Année en cours"
              value={statsPeriodes.annee_courante}
              subtitle={new Date().getFullYear().toString()}
              color={colors.success}
              icon={<CalendarToday />}
            />
          </Grid>
        </Grid>
      )}

      {/* Tableau des dépenses */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, color: colors.primary, fontWeight: 600 }}>
          Détail des Dépenses ({depensesData.length} résultat(s))
        </Typography>
        
        <MaterialTable
          columns={columns}
          data={depensesData}
          isLoading={loading}
          editable={{
            onRowAdd: handleRowAdd,
            onRowUpdate: handleRowUpdate,
            onRowDelete: handleRowDelete,
          }}
          options={{
            sorting: true,
            search: true,
            paging: true,
            pageSize: 10,
            pageSizeOptions: [5, 10, 20],
            actionsColumnIndex: -1,
            headerStyle: {
              backgroundColor: colors.primary,
              color: 'white',
              fontWeight: 600
            },
          }}
          localization={{
            body: {
              emptyDataSourceMessage: "Aucune dépense à afficher",
              addTooltip: "Ajouter",
              deleteTooltip: "Supprimer",
              editTooltip: "Modifier",
            },
            header: {
              actions: 'Actions'
            },
            pagination: {
              labelDisplayedRows: '{from}-{to} sur {count}',
              labelRowsSelect: 'lignes'
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default GestionDepenses;